import { useState, type ChangeEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CanAccess, useDataProvider, useNotify, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { formatInstant } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type {
  WhatsAppPersonalLoadResult,
  WhatsAppPersonalNumberRow,
} from "../types";
import {
  MAX_PERSONAL_NUMBERS_PER_LOAD,
  parsePersonalNumbers,
} from "./unclassifiedRules";

const PER_PAGE = 25;

const LoadCard = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<WhatsAppPersonalLoadResult | null>(null);

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) setText(await file.text());
    event.target.value = "";
  };

  const onLoad = async () => {
    setFailure(null);
    setResult(null);
    const numbers = parsePersonalNumbers(text);
    if (numbers.length === 0) {
      setFailure(translate("crm.whatsapp.numbers.empty_input"));
      return;
    }
    if (numbers.length > MAX_PERSONAL_NUMBERS_PER_LOAD) {
      setFailure(
        translate("crm.whatsapp.numbers.too_many", {
          max: MAX_PERSONAL_NUMBERS_PER_LOAD,
        }),
      );
      return;
    }
    setSaving(true);
    try {
      setResult(await dataProvider.loadWhatsAppPersonalNumbers(numbers));
      setText("");
      await queryClient.invalidateQueries({ queryKey: ["whatsapp"] });
    } catch (error) {
      setFailure(
        error instanceof Error && error.message
          ? error.message
          : translate("crm.whatsapp.numbers.load_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <Label htmlFor="personal-numbers-text" className="text-base">
          {translate("crm.whatsapp.numbers.paste_label")}
        </Label>
        <Textarea
          id="personal-numbers-text"
          rows={6}
          className="text-base"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <p className="text-[15px] text-muted-foreground">
          {translate("crm.whatsapp.numbers.paste_help")}
        </p>
        <Label htmlFor="personal-numbers-file" className="text-base">
          {translate("crm.whatsapp.numbers.file_label")}
        </Label>
        <input
          id="personal-numbers-file"
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          className="block min-h-11 w-full text-base"
          onChange={onFile}
        />
        <Button
          type="button"
          className="h-11"
          disabled={saving}
          onClick={onLoad}
        >
          {translate("crm.whatsapp.numbers.load")}
        </Button>
        {failure ? (
          <p role="alert" className="text-[15px] text-destructive">
            {failure}
          </p>
        ) : null}
        {result ? (
          <p role="status" className="text-base font-medium">
            {translate("crm.whatsapp.numbers.result", result)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};

const RemoveDialog = ({
  row,
  onClose,
}: {
  row: WhatsAppPersonalNumberRow | null;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const onConfirm = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await dataProvider.removeWhatsAppPersonalNumber(row.phone);
      await queryClient.invalidateQueries({ queryKey: ["whatsapp"] });
      notify("crm.whatsapp.numbers.removed", { type: "success" });
      onClose();
    } catch {
      notify("crm.whatsapp.numbers.remove_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={row != null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("crm.whatsapp.numbers.remove")}</DialogTitle>
          <DialogDescription>
            {translate("crm.whatsapp.numbers.remove_help")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={onClose}
          >
            {translate("crm.whatsapp.unclassified.cancel")}
          </Button>
          <Button
            type="button"
            className="h-11"
            disabled={saving}
            onClick={onConfirm}
          >
            {translate("crm.whatsapp.numbers.remove_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NumbersList = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [page, setPage] = useState(1);
  const [removing, setRemoving] = useState<WhatsAppPersonalNumberRow | null>(
    null,
  );
  const list = useQuery({
    queryKey: ["whatsapp", "personal-numbers", page],
    queryFn: () =>
      dataProvider.listWhatsAppPersonalNumbers({ page, perPage: PER_PAGE }),
    retry: false,
  });

  if (list.error) {
    return (
      <ListErrorState
        message={translate("crm.whatsapp.numbers.error")}
        onRetry={() => list.refetch()}
      />
    );
  }
  if (!list.data) return <ListCardsSkeleton rows={3} />;
  if (list.data.rows.length === 0 && page === 1) {
    return <ListEmptyState title={translate("crm.whatsapp.numbers.empty")} />;
  }
  const pages = Math.max(1, Math.ceil(list.data.total / PER_PAGE));
  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {list.data.rows.map((row) => (
          <li key={row.phone}>
            <Card data-testid="whatsapp-personal-number">
              <CardContent className="space-y-1 p-4">
                <p className="text-base font-bold">
                  {translate("crm.whatsapp.numbers.ending", {
                    last4: row.phoneLast4,
                  })}
                </p>
                <p className="text-[15px] text-muted-foreground">
                  {translate("crm.whatsapp.numbers.dropped", {
                    count: row.droppedCount,
                  })}
                </p>
                <p className="text-[15px] text-muted-foreground">
                  {row.lastSeenAt
                    ? translate("crm.whatsapp.numbers.last_seen", {
                        at: formatInstant(row.lastSeenAt),
                      })
                    : translate("crm.whatsapp.numbers.never_seen")}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11"
                  onClick={() => setRemoving(row)}
                >
                  {translate("crm.whatsapp.numbers.remove")}
                </Button>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
      {pages > 1 ? (
        <nav
          className="flex items-center justify-between gap-2"
          aria-label={translate("crm.whatsapp.inbox.pagination")}
        >
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {translate("crm.whatsapp.inbox.previous")}
          </Button>
          <span className="text-[15px] text-muted-foreground">
            {translate("crm.whatsapp.inbox.page_of", { page, pages })}
          </span>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            {translate("crm.whatsapp.inbox.next")}
          </Button>
        </nav>
      ) : null}
      <RemoveDialog row={removing} onClose={() => setRemoving(null)} />
    </div>
  );
};

/**
 * Almikia Epic EW3 (TASK-095): the personal-numbers list screen. Paste or upload numbers (one per
 * line, comma or semicolon, optional header); the counts loaded / duplicates / rejected come back
 * from the server rules. The list shows the last 4 digits only, the dropped-message counter and
 * the last-seen date. Gated like the tray: only the holder of the per-person permission.
 */
export const PersonalNumbersScreen = () => {
  const translate = useTranslate();
  return (
    <CanAccess
      resource="whatsapp_personal_numbers"
      action="list"
      accessDenied={<Navigate to="/" replace />}
    >
      <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
        <h1 className="px-1 text-2xl font-bold">
          {translate("crm.whatsapp.numbers.title")}
        </h1>
        <p className="px-1 text-[15px] text-muted-foreground">
          {translate("crm.whatsapp.numbers.intro")}
        </p>
        <Button asChild variant="outline" className="h-11">
          <Link to="/whatsapp_unclassified">
            {translate("crm.whatsapp.numbers.tray_link")}
          </Link>
        </Button>
        <LoadCard />
        <h2 className="px-1 text-lg font-semibold">
          {translate("crm.whatsapp.numbers.list_title")}
        </h2>
        <NumbersList />
      </div>
    </CanAccess>
  );
};

PersonalNumbersScreen.path = "/whatsapp_personal_numbers";
